import React from 'react';
import { FindingsTableRow } from './FindingsTableRow';
import { Checkbox } from '../ui/Checkbox';
import { TablePagination } from '../ui/TablePagination';
import { Membership } from 'api-client';

interface Finding {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'resolved' | 'in_progress' | 'false_positive' | 'duplicate';
  asset: string;
  discoveredAt: string;
  assignee?: {
    id: string;
    name: string;
    role: string;
    avatar?: string;
  };
  description: string;
}

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface FindingsTableProps {
  findings: any;
  selectedFindings: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectFinding: (id: string, selected: boolean) => void;
  onAssignFinding: (id: string, assigneeId: string) => void;
  onAction: (id: string, action: string) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalFindings: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
  assignees: Array<Membership>;
}

export const FindingsTable: React.FC<FindingsTableProps> = ({
  findings,
  selectedFindings,
  onSelectAll,
  onSelectFinding,
  onAssignFinding,
  onAction,
  currentPage,
  onPageChange,
  totalFindings,
  itemsPerPage,
  paginationInfo,
  assignees,
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalFindings);

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden">
      {findings.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-6 py-4 text-left">
                    <Checkbox
                      checked={selectedFindings.length === findings.length}
                      onCheckedChange={(checked) => onSelectAll(!!checked)}
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Severity</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Asset</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Discovered</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Assignee</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {findings.map((finding: any) => (
                  <FindingsTableRow
                    key={finding.id}
                    finding={finding}
                    isSelected={selectedFindings.includes(finding.id)}
                    onSelect={onSelectFinding}
                    onAssign={onAssignFinding}
                    onAction={onAction}
                    assignees={assignees}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={currentPage}
            onPageChange={onPageChange}
            totalItems={totalFindings}
            itemsPerPage={itemsPerPage}
            paginationInfo={paginationInfo}
            itemName="findings"
          />
        </>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 text-gray-400 flex items-center justify-center">
              No findings found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};